/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Fragment, useState, useEffect, useContext, createRef } from 'react';
import * as t from 'lib/t';
import api from 'lib/api';
import NonEmptyObject from 'lib/NonEmptyObject';
import * as I from 'immutable';
import underscored from 'underscore.string/underscored';
import resourceTypes from 'lib/resource';
import { Fields } from 'lib/proforma';
import autosize from 'autosize';
import './global.scss';
import pluralize from 'pluralize';
import { camelizeKeys } from 'humps';
import Task from 'folktale/concurrency/task';
import BuilderContext from '../Context';
import commands from '../commands';
import DestroyBtn from './DestroyBtn';
import ActionBtn from './ActionBtn';
import processException from 'lib/processException';
import FeedbackBand from './FeedbackBand';
import Errors from './Errors';
import buildErrors from './buildErrors';
import GoogleButtonRow from './GoogleButtonRow';
import PATHS from 'const/paths';
import { set } from 'ops';
import toast from 'lib/toast';

// -------------------------------

type PropsT = {
  id?: ID,
  resourceName: string,
  requestClosePortal: ?Function,
};

type FormElementT = {
  getValue(): null | Object,
  validate(): {
    errors: {
      path: string[],
      actual: any,
      expected: { displayName: string },
    }[],
  },
};

// Find the closest DOM element with non-zero scrollTop and
// set it to 0
const scrollTop = (el) => {
  el &&
    $(el)
      .parents()
      .each((i, v) => {
        v.scrollTop = 0;
      });
};

const getFormType = (formValue, ns) => {
  if (ns !== 'listing') return t.struct({ [ns]: resourceTypes[ns] });
  // Check if the listingType == 'For Lease', and instead use
  // listing.forLease
  type ListingType = 'For Lease' | 'For Sale' | 'For Lease & For Sale' | void;
  const lt: ListingType = I.getIn(formValue, ['listing', 'listingType']);
  const { listing } = resourceTypes;
  const o = {
    'For Lease': listing.forLease,
    'For Sale': listing.forSale,
    'For Lease & For Sale': listing.forLeaseAndSale,
  };
  return t.struct({
    listing: lt ? o[lt] : listing.default,
  });
};

const Component = (props: PropsT) => {
  const ctx = useContext(BuilderContext);
  const fieldsRef = createRef();
  const htmlForm = createRef();
  let { resource, resourceName, mode } = ctx.state;
  resourceName = resourceName || props.resourceName;

  let id = resource.get('id');
  id = id || props.id;

  const { requestClosePortal } = props;
  const [isLoading, setLoading] = useState(false);
  const [localResource, setResource] = useState({});
  const [errors, setErrors] = useState([]);
  const loadResource = Task.fromPromised(() =>
    api.fetchJson(resourcePath + '.json')
  );
  const ns = underscored(resourceName);
  const pluralNS = pluralize(ns);
  const resourcePath = id ? `/${pluralNS}/${id}` : `/${pluralNS}`;

  const formValue: any = NonEmptyObject.is(localResource)
    ? localResource
    : { [ns]: { ...localResource, ...resource.toJS() } };
  const formType = getFormType(formValue, ns);

  useEffect(() => {
    autosize(document.querySelectorAll('textarea'));

    // Check if there is an id. If there is, load the data
    if (id) {
      setLoading(true);
      const taskExecution = loadResource().run();
      taskExecution.listen({
        onResolved: (res) => {
          let payload = camelizeKeys(res[ns]);
          if (mode === 'DUP_FORM') {
            // Remove id from the payload
            delete payload.id;
            // Remove 'documents_attributes' from the payload
            delete payload.documents_attributes;
          }

          setResource({ [ns]: payload });
          setLoading(false);
        },
        onRejected: (e) => {
          setLoading(false);
          setErrors([typeof e === 'string' ? e : e.message]);
        },
        onCancelled: () => {},
      });
    }

    return () => {
      setLoading(false);
    };
  }, [id, resourceName, mode]);

  function submitForm(e) {
    e && e.preventDefault();

    if (!htmlForm.current) return;
    if (!fieldsRef.current) return;
    const form: FormElementT = fieldsRef.current;
    const validation = form.validate();

    // Show errors
    if (validation.errors.length !== 0) {
      const errs = buildErrors(fieldsRef.current);
      setErrors(errs);
      scrollTop(htmlForm.current);
      return;
    }

    const sendForm = Task.fromPromised(() => {
      const isUpdate = id && mode !== 'DUP_FORM';
      const method = isUpdate ? 'PUT' : 'POST';
      const path = isUpdate && id ? `/${pluralNS}/${id}` : `/${pluralNS}`;
      return api.fetch(path, { method, body: formValue });
    });

    setLoading(true);

    const taskExecution = sendForm().run();

    taskExecution.listen({
      onResolved() {
        setLoading(false);
        setErrors([]);
        const closer = requestClosePortal;
        closer && closer();
        // Refetch the projects
        set(PATHS.projectRefetchHash, Math.random());
        const action = id ? 'updated' : 'created';
        toast(`Record ${action}.`);
      },
      onRejected(err) {
        processException(err).then(setErrors);
        setLoading(false);
      },
      onCancelled() {},
    });

    taskExecution.promise().then(() => {});
  }

  return (
    <Fragment>
      {process.env.NODE_ENV !== 'test' && (
        <GoogleButtonRow resource={localResource} resourceName={ns} />
      )}
      <form
        className="ResourceForm"
        data-testid="ResourceForm"
        css={css(`
          position: relative;
          padding-bottom: 45px;
          overflow: hidden;
          legend {
            font-size: 1em;
            color: #bdbdbd;
          }
        `)}
        onSubmit={submitForm}
        ref={htmlForm}
      >
        <div className="d-flex justify-content-end">
          <DestroyBtn onError={setErrors} />
        </div>
        <Errors isLoading={isLoading} errors={errors} />
        <FeedbackBand isLoading={isLoading} />
        <div className="row">
          <div
            className="col-lg-12 col-md-12"
            css={css(`
              legend {
                border: none;
                margin-bottom: 1rem;
              }

              label.col-form-label,
              legend {
                font-size: 1.6rem;
                color: #999;
                .required {
                  color: red;
                }
              }

              .alert-danger ul {
                margin-bottom: 0;
                padding-left: 2rem;
              }

              .form-group.has-error .error-block {
                display: none;
              }

              .required::after {
                content: ' (required)';
                color: #ccc;
                font-size: 0.8em;
              }
            `)}
          >
            <Fields
              ref={(el) => {
                if (el) {
                  // $FlowFixMe[incompatible-type]
                  fieldsRef.current = el;
                }
              }}
              type={formType}
              value={formValue}
              onChange={(v) => {
                commands(ctx).set('mutated', true);
                setResource(v);
              }}
            />
          </div>
        </div>
      </form>
      <ActionBtn submitForm={submitForm} isLoading={isLoading} />
    </Fragment>
  );
};

export default Component;
